
import { useEffect, useRef } from 'react'
import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ui/icon'
import { cn } from '@/lib/utils'

interface CanvasListFooterProps {
  /** How many sessions are loaded right now. */
  loaded: number
  /** How many the filter matches in total — the number the user is told. */
  total: number
  hasMore: boolean
  isLoadingMore: boolean
  onLoadMore: () => void
  className?: string
}

/**
 * The count line under a session grid, plus the control that pulls in the next page.
 *
 * The next page is requested as soon as the sentinel scrolls into view; the button stays as the
 * way forward for keyboard users and for browsers without IntersectionObserver.
 */
export function CanvasListFooter({
  loaded,
  total,
  hasMore,
  isLoadingMore,
  onLoadMore,
  className,
}: CanvasListFooterProps) {
  const sentinelRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel || !hasMore || isLoadingMore) return
    if (typeof IntersectionObserver !== 'function') return

    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) onLoadMore()
      },
      { rootMargin: '400px' },
    )
    observer.observe(sentinel)

    return () => observer.disconnect()
  }, [hasMore, isLoadingMore, onLoadMore])

  if (total === 0) return null

  return (
    <div className={cn('flex flex-col items-center gap-3 py-2', className)}>
      <p className="text-sm text-muted-foreground" aria-live="polite">
        {hasMore
          ? `Showing ${loaded} of ${total} session${total === 1 ? '' : 's'}`
          : `${total} session${total === 1 ? '' : 's'}`}
      </p>

      {hasMore && (
        <>
          <div ref={sentinelRef} aria-hidden="true" className="h-px w-full" />
          <Button variant="outline" size="sm" onClick={onLoadMore} disabled={isLoadingMore}>
            {isLoadingMore ? (
              <>
                <Icon name="loader-2" className="mr-1 h-4 w-4 animate-spin" />
                Loading...
              </>
            ) : (
              'Load more'
            )}
          </Button>
        </>
      )}
    </div>
  )
}
